/**
 * DriveApp — root Preact component for the Drive file manager UI.
 *
 * Establishes DriveStoreContext so all child components can read the reactive
 * store without prop drilling. Mounted into #drive-preact-root by drive_controller.js.
 *
 * Props:
 *   store     {object} — driveStore instance created by createDriveStore()
 *   i18n      {object} — I18n instance from drive_controller.js
 *   canUpdate {boolean} — DriveFolderPolicy#update? (rename/move menu)
 */

import { h } from 'preact'
import { useCallback, useMemo, useState } from 'preact/hooks'
import { DriveStoreContext, DriveI18nContext, DriveUiContext } from './drive-context.js'
import { DriveContent } from './DriveContent.jsx'
import { RenameModal } from './RenameModal.jsx'

export { DriveStoreContext, DriveI18nContext, DriveUiContext, useDriveStore, useDriveI18n, useDriveUi } from './drive-context.js'

export function DriveApp({ store, i18n, canUpdate = false }) {
  const [renameTarget, setRenameTarget] = useState(null)

  const openRename = useCallback((target) => {
    setRenameTarget(target)
  }, [])

  const closeRename = useCallback(() => {
    setRenameTarget(null)
  }, [])

  const requestMove = useCallback((itemKey) => {
    store.selectOnly(itemKey)
    document.dispatchEvent(new CustomEvent('drive:open-move'))
  }, [store])

  const ui = useMemo(() => ({
    canUpdate: !!canUpdate,
    openRename,
    requestMove
  }), [canUpdate, openRename, requestMove])

  return (
    <DriveStoreContext.Provider value={store}>
      <DriveI18nContext.Provider value={i18n}>
        <DriveUiContext.Provider value={ui}>
          <DriveContent />
          <RenameModal target={renameTarget} onClose={closeRename} />
        </DriveUiContext.Provider>
      </DriveI18nContext.Provider>
    </DriveStoreContext.Provider>
  )
}
